<template>
  <div>
    <div>
      <app-radio-group
        :active-option="shareType"
        :options="shareTypeOptions"
        @change-active-option="onShareTypeChange"
      />
    </div>

    <div v-if="shareType === SHARE_TYPE_OPTIONS.CUSTOM" class="mt-5">
      <h3 class="custom-rights-label">{{ $t(`${TRANSLATION_KEY_PREFIX}.membersWithAccess`) }}</h3>
      <MembersAutocomplete
        class="mb-2"
        :items="allRoomMembers"
        :placeholder="$t(`${TRANSLATION_KEY_PREFIX}.searchMembersPlaceholder`)"
        @select="onMemberSelect"
        hide-details
      />
      <DocumentSharePicker :force-pm-selection="true" :force-current-user-selection="true" class="document-share-picker"/>
    </div>

    <v-divider color="grey-100" opacity="1" class="mt-5"/>

    <app-toggle :value="customMessageIsEnabled"
                data-locator="add-custom-message-toggle"
                class="mt-5"
                :label="$t('project.question-answer.QuestionAnswerAddShare.sendCustomMessage')"
                color="darkblue-500"
                iconColor="darkblue-500"
                iconSize="xxxs"
                hide-details
                @update:model-value="customMessageIsEnabled = $event"
    />

    <div v-if="customMessageIsEnabled" class="mt-4">
      <VisualTextEditor v-model:value="notifyMessage"
                        qa-variant
                        counter="footer"
                        :qa-variant-error="customMessageValidation.$invalid && customMessageValidation.$dirty"
                        :placeholder="$t('project.question-answer.QuestionAnswerAddShare.customMessagePlaceholder')"
                        :max-length="NOTIFY_MESSAGE_MAX_LENGTH"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import { formFieldErrors } from '@/common/utils/form'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { maxEscapedLength } from '@/common/utils/validators'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import MembersAutocomplete from '@/project/members/MembersAutocomplete.vue'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { DocumentSharePickerMutationTypes } from '@/store/modules/document-share-picker/mutation_types.ts'

const TRANSLATION_KEY_PREFIX = 'project.question-answer.QuestionAnswerAddShare'
const NOTIFY_MESSAGE_MAX_LENGTH = 3000

export const validations = {
  customShareMessage: {
    maxLength: maxEscapedLength(NOTIFY_MESSAGE_MAX_LENGTH),
  },
}

export default {
  name: 'QuestionAnswerAddShare',
  components: { DocumentSharePicker, MembersAutocomplete, VisualTextEditor },
  props: {
    customMessage: {
      type: String,
      required: true,
    },
    customMessageValidation: {
      type: Object,
      required: true,
    },
    shareType: {
      type: String,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      NOTIFY_MESSAGE_MAX_LENGTH,
      SHARE_TYPE_OPTIONS,
      customMessageIsEnabled: !!this.customMessage.length,
    }
  },
  computed: {
    ...mapState('groups', ['groups']),
    ...mapGetters('groups', ['allRoomMembers']),
    ...mapGetters('room', ['roomMnemo', 'isCurrentUserPm']),
    ...mapGetters('user', ['currentUserId']),
    notifyMessage: {
      get () { return this.customMessage },
      set (value) { this.$emit('update:customMessage', value) },
    },
    notifyMessageErrors () {
      return formFieldErrors(
        this.customMessageValidation,
        [
          [
            'maxLength',
            this.$t('common.validations.maxLength', {
              fieldName: this.$t('project.question-answer.QuestionAnswerAddShare.customMessage'),
              maxLength: NOTIFY_MESSAGE_MAX_LENGTH,
            }),
          ],
        ],
      )
    },
    shareTypeOptions () {
      const shareTypeOptions = Object.values(SHARE_TYPE_OPTIONS).filter(
        option => option !== SHARE_TYPE_OPTIONS.PRIVATE && option !== SHARE_TYPE_OPTIONS.PUBLIC,
      )

      return shareTypeOptions.map(option => {
        return {
          name: this.$t(`${TRANSLATION_KEY_PREFIX}.${option}`),
          value: option,
        }
      })
    },
  },
  watch: {
    customMessageIsEnabled (value) {
      if (!value) {
        this.notifyMessage = ''
      }
    },
  },
  beforeUnmount () {
    this.RESET_SELECTED_GROUPS_AND_MEMBERS()
  },
  async created () {
    // If question is being edited, do not fetch and set selected groups and members
    // they will be set from the parent component with the data from the question
    if (!this.isEditMode) {
      await this.initializeGroupsAndMembers()
    }
  },
  methods: {
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.RESET_SELECTED_GROUPS_AND_MEMBERS,
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      DocumentSharePickerActionTypes.HIGHLIGHT_MEMBER,
    ]),
    ...mapMutations('documentSharePicker', [
      DocumentSharePickerMutationTypes.ADD_MEMBERS_TO_SELECTED,
    ]),
    ...mapActions('groups', ['GET_GROUPS']),
    async initializeGroupsAndMembers () {
      await this.GET_GROUPS(this.roomMnemo)
      const pmGroup = this.groups.find(g => g.isPm)
      if (pmGroup) {
        this.INIT_SELECTED_GROUPS([pmGroup])
      }
      // Add current user to selected members
      this.INIT_SELECTED_MEMBERS([{ id: this.currentUserId }])
    },
    onShareTypeChange(value) {
      this.$emit('update:shareType', value)
    },
    onMemberSelect (member) {
      if (member) {
        this.ADD_MEMBERS_TO_SELECTED([member])
        this.HIGHLIGHT_MEMBER(member)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.qa-share-radio {
  padding: 16px 24px;

  &--active {
    background-color: rgb(var(--v-theme-primary-lighten-5));
  }
}

.custom-label-title {
  font-size: 16px;
  font-weight: 500;
}

.AppToggle {
  :deep(.v-switch__track) {
    height: 20px;
    min-width: 36px;
    opacity: 1;

    &.bg-primary {
      opacity: 1;
    }
  }

  :deep(.v-selection-control__input) {
    width: 30px;
    height: 30px;
  }

  :deep(.v-switch__thumb) {
    height: 15px;
    width: 15px;
  }

  :deep(.v-selection-control) {
    min-height: 20px;
  }

  :deep(.v-selection-control__wrapper) {
    height: 20px;
  }

  :deep(*) {
    .v-selection-control__input {
      transform: translateX(-7px) !important;
    }

    .v-selection-control--dirty .v-selection-control__input {
      transform: translateX(7px) !important;
    }
  }
}

.document-share-picker {
  :deep(.v-selection-control--disabled .v-selection-control__input) {
    color: rgb(var(--v-theme-darkblue-200));
  }

  :deep(.v-selection-control--dirty .v-selection-control__input) {
    color: rgb(var(--v-theme-darkblue-500));
  }
}

.custom-rights-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgb(var(--v-theme-grey-900));
}

:deep(input) {
  &::placeholder {
    color: rgb(var(--v-theme-grey-500));
    opacity: 1;
  }
}
</style>
